import { useEffect, useRef, useState } from 'react';
import { Search, Download, X } from 'lucide-react';
import LoadingSpinner from '../Shared/LoadingSpinner';
import { downloadXlsx } from './download';
import { fmtChange, fmtMoney, fmtNumber, fmtPct } from './format';
import { c, btn, card } from '../../theme';

// The customers, one row each: current bill, projected, and the change. The
// row is a button (keyboard-reachable) that opens the customer on the right.
export default function CustomerList({
  data, loading, refreshing, error, selected, onSelect, bandLabel, onClearBand,
  category, categoryLabel, onClearCategory,
  search, onSearch, sort, onSort, page, pageSize, onPage, exportParams,
}) {
  // Typed text is held locally and sent after a pause, so a search is one
  // request per word rather than one per keystroke.
  // The callback is read through a ref: the page hands down a new function on
  // every render, and restarting the timer on each would hold a search back
  // for as long as anything else on the page kept re-rendering.
  const [draft, setDraft] = useState(search);
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;
  useEffect(() => { setDraft(search); }, [search]);
  useEffect(() => {
    if (draft.trim() === search) return undefined;
    const id = setTimeout(() => onSearchRef.current(draft.trim()), 300);
    return () => clearTimeout(id);
  }, [draft, search]);

  const [exporting, setExporting] = useState(false);
  const rows = data?.rows || [];
  const total = data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <section style={s.card}>
      <style>{css}</style>
      <div style={s.head}>
        <div style={s.headRow}>
          <span style={s.title}>Customers</span>
          <span style={s.count}>{fmtNumber(total)}</span>
        </div>
        <label style={s.search}>
          <Search size={14} color={c.textFaint} aria-hidden="true" />
          <input id="billing-search" type="search" value={draft} placeholder="Search account #, e.g. 1M-712274"
                 aria-label="Search customers" onChange={(e) => setDraft(e.target.value)} style={s.searchInput} />
        </label>
        <div style={{ ...s.headRow, flexWrap: 'wrap', justifyContent: 'flex-start' }}>
          <select id="billing-sort" aria-label="Sort customers" value={sort} onChange={(e) => onSort(e.target.value)} style={s.sort}>
            <option value="change_desc">Largest increase</option>
            <option value="pct_desc">Largest % increase</option>
            <option value="change_asc">Largest decrease</option>
            <option value="current_desc">Largest bill</option>
            <option value="account">Account number</option>
            {category && <option value="category_desc">Largest {categoryLabel?.toLowerCase()} charge</option>}
          </select>
          {categoryLabel && (
            <button type="button" style={s.chip} onClick={onClearCategory} aria-label={`Clear filter ${categoryLabel}`}>
              {categoryLabel} <X size={12} />
            </button>
          )}
          {bandLabel && (
            <button type="button" style={s.chip} onClick={onClearBand} aria-label={`Clear filter ${bandLabel}`}>
              {bandLabel} <X size={12} />
            </button>
          )}
        </div>
      </div>

      {loading ? <LoadingSpinner /> : error ? (
        <div style={s.error}>{error.message}</div>
      ) : !rows.length ? (
        <div style={s.empty}>No customers match.</div>
      ) : (
        // Dimmed while a new scenario or filter loads: the rows on screen are
        // the previous answer, and must not pass for the new one.
        <div role="list" aria-busy={refreshing || undefined}
             style={{ ...s.scroll, opacity: refreshing ? 0.45 : 1 }}>
          {rows.map((r) => {
            const on = r.account === selected;
            return (
              <button key={r.account} type="button" role="listitem" className="cb-row"
                      aria-current={on ? 'true' : undefined} onClick={() => onSelect(r.account)}
                      style={on ? s.rowOn : undefined}>
                <span style={{ minWidth: 0 }}>
                  <span style={s.acct}>{r.account}</span>
                  <span style={s.sub}>{[r.cycle, r.price_list].filter(Boolean).join(' · ')} · {fmtMoney(r.current)} → {fmtMoney(r.projected)}</span>
                  {category && r.category_amount !== null && (
                    <span style={s.catLine}>{categoryLabel}: {fmtMoney(r.category_amount)} · {r.category_lines} line{r.category_lines === 1 ? '' : 's'}</span>
                  )}
                </span>
                <span style={{ textAlign: 'right' }}>
                  <span style={{ ...s.chg, color: r.change >= 0.005 ? c.successText : r.change <= -0.005 ? c.dangerText : c.textFaint }}>{fmtChange(r.change)}</span>
                  <span style={s.sub}>{r.pct === null ? '' : fmtPct(r.pct)}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div style={s.foot}>
        <span>
          {total ? `${fmtNumber(page * pageSize + 1)}–${fmtNumber(Math.min(total, (page + 1) * pageSize))} of ${fmtNumber(total)}` : ''}
        </span>
        <span style={{ display: 'flex', gap: '6px' }}>
          <button type="button" style={s.pageBtn} disabled={page === 0} onClick={() => onPage(page - 1)} aria-label="Previous page">‹</button>
          <button type="button" style={s.pageBtn} disabled={page + 1 >= pages} onClick={() => onPage(page + 1)} aria-label="Next page">›</button>
          <button type="button" style={s.exportBtn} disabled={exporting || !total}
                  onClick={async () => { setExporting(true); await downloadXlsx('/customer-billing/customers/export.xlsx', exportParams, 'customer-billing.xlsx'); setExporting(false); }}>
            <Download size={12} /> {exporting ? 'Exporting…' : 'Export'}
          </button>
        </span>
      </div>
    </section>
  );
}

const css = `
.cb-row {
  display: flex; justify-content: space-between; align-items: center; gap: 10px;
  width: 100%; padding: 9px 14px; border: 0; border-bottom: 1px solid ${c.canvas};
  background: ${c.surface}; font-family: inherit; text-align: left; cursor: pointer; color: ${c.text};
}
.cb-row:hover { background: ${c.surfaceSunk}; }
`;

const s = {
  card:     { ...card.sm, overflow: 'hidden' },
  head:     { padding: '11px 14px', background: c.surfaceSunk, borderBottom: `1px solid ${c.border}`, display: 'flex', flexDirection: 'column', gap: '8px' },
  headRow:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' },
  title:    { fontSize: '13px', fontWeight: 600, color: c.text },
  count:    { fontSize: '11.5px', color: c.textMuted, fontVariantNumeric: 'tabular-nums' },
  search:   { display: 'flex', alignItems: 'center', gap: '6px', height: '32px', padding: '0 10px', border: `1px solid ${c.borderStrong}`, borderRadius: '6px', background: c.surface },
  searchInput: { border: 0, outline: 'none', fontFamily: 'inherit', fontSize: '12.5px', flex: 1, minWidth: 0, background: 'transparent' },
  sort:     { height: '28px', fontSize: '12px', fontFamily: 'inherit', padding: '0 6px', border: `1px solid ${c.border}`, borderRadius: '6px', background: c.surface, color: c.textStrong },
  chip:     { ...btn.secondaryXs, gap: '4px', background: c.infoTint, color: c.info, borderColor: '#BFDBFE' },
  // Its own scroll, so fifty customers do not stretch the page far past the
  // detail beside them.
  scroll:   { maxHeight: '760px', overflowY: 'auto', transition: 'opacity 0.15s' },
  rowOn:    { background: c.infoTint, boxShadow: `inset 3px 0 0 ${c.info}` },
  acct:     { display: 'block', fontSize: '13px', fontWeight: 600 },
  sub:      { display: 'block', fontSize: '11px', color: c.textMuted, marginTop: '2px', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  catLine:  { display: 'block', fontSize: '11px', color: c.info, marginTop: '2px', fontVariantNumeric: 'tabular-nums' },
  chg:      { display: 'block', fontSize: '13px', fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  foot:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', padding: '8px 14px', fontSize: '11.5px', color: c.textMuted, borderTop: `1px solid ${c.border}` },
  pageBtn:  { ...btn.secondaryXs, padding: '3px 9px' },
  exportBtn:{ ...btn.secondaryXs },
  empty:    { padding: '32px 14px', textAlign: 'center', fontSize: '12.5px', color: c.textMuted },
  error:    { padding: '14px', color: c.dangerText, fontSize: '12.5px' },
};
